import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { proyectosApi, clientesApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import ClienteAlegraSelect from '../components/ClienteAlegraSelect';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from '../hooks/use-toast';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';

export const ProyectoForm = () => {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    codigo: '',
    descripcion: '',
    cliente_id: '',
    estado: 'VIGENTE',
    fecha_inicio: '',
    fecha_termino: '',
    contrato: '',
  });
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadInitialData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadInitialData = async () => {
    try {
      const clientesRes = await clientesApi.getAll();
      setClientes(clientesRes.data);

      if (isEdit) {
        const proyectoRes = await proyectosApi.getOne(id);
        const p = proyectoRes.data || {};
        setFormData({
          codigo: p.codigo || '',
          descripcion: p.descripcion || '',
          cliente_id: p.cliente_id || '',
          cliente_nombre: p.cliente_nombre || '',
          cliente_alegra_id: p.cliente_alegra_id || '',
          cliente_rut: p.cliente_rut || '',
          estado: p.estado || 'VIGENTE',
          fecha_inicio: p.fecha_inicio || '',
          fecha_termino: p.fecha_termino || '',
          contrato: p.contrato || '',
        });
      }
    } catch (error) {

      toast({ title: 'Error al cargar los datos', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Para edición, solo validar codigo y descripcion
    // cliente_id es opcional si ya existe cliente_nombre
    if (!formData.codigo || !formData.descripcion) {
      toast({ title: 'Complete los campos obligatorios (código y descripción)', variant: 'destructive' });
      return;
    }
    
    // Si es nuevo proyecto, debe haber cliente seleccionado (Alegra o ERP — Iter 307)
    if (!isEdit && !formData.cliente_alegra_id && !formData.cliente_id) {
      toast({ title: 'Seleccione un cliente (o créelo con "Crear cliente nuevo")', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      // Preparar datos para enviar
      const dataToSend = { ...formData };
      
      // Si cliente_id está vacío pero hay cliente_nombre, enviamos sin cliente_id
      // El backend buscará el cliente por nombre
      if (!dataToSend.cliente_id && formData.cliente_nombre) {
        dataToSend.cliente_nombre = formData.cliente_nombre;
      }
      
      if (isEdit) {
        await proyectosApi.update(id, dataToSend);
        toast({ title: 'Proyecto actualizado' });
      } else {
        await proyectosApi.create(dataToSend);
        toast({ title: 'Proyecto creado' });
      }
      navigate('/proyectos');
    } catch (error) {

      toast({ title: error.response?.data?.detail || 'Error al guardar', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (<div className="space-y-6"><Skeleton className="h-10 w-64" /><Skeleton className="h-[400px]" /></div>);
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="proyecto-form">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">
            {isEdit ? 'Editar Proyecto' : 'Nuevo Proyecto'}
          </h1>
          <p className="text-muted-foreground">
            {isEdit ? `Editando ${formData.codigo}` : 'Complete el formulario para crear un nuevo proyecto'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle className="font-heading">Datos del Proyecto</CardTitle>
            <CardDescription>Información principal del proyecto</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="codigo">Código Proyecto *</Label>
              <Input id="codigo" type="number" value={formData.codigo} onChange={(e) => handleChange('codigo', e.target.value)} placeholder="Ej: 9999" data-testid="input-codigo" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="cliente_id">Cliente *</Label>
              <ClienteAlegraSelect
                value={(formData.cliente_alegra_id || formData.cliente_id) ? { alegra_id: formData.cliente_alegra_id, cliente_id: formData.cliente_id, nombre: formData.cliente_nombre, rut: formData.cliente_rut } : null}
                onChange={(sel) => setFormData((d) => ({
                  ...d,
                  cliente_alegra_id: sel?.alegra_id || '',
                  cliente_id: sel?.cliente_id || '',
                  cliente_nombre: sel?.nombre || '',
                  cliente_rut: sel?.rut || '',
                }))}
              />
              <p className="text-xs text-muted-foreground">
                Clientes de Alegra y del ERP. Si no existe, créalo con "Crear cliente nuevo": queda disponible al instante y se sube a Alegra en la próxima sincronización.
              </p>
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="descripcion">Descripción *</Label>
              <Textarea id="descripcion" value={formData.descripcion} onChange={(e) => handleChange('descripcion', e.target.value)} placeholder="Descripción del proyecto" rows={3} data-testid="input-descripcion" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="estado">Estado</Label>
              <select
                value={formData.estado}
                onChange={(e) => handleChange('estado', e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="select-estado"
              >
                <option value="VIGENTE">Vigente</option>
                <option value="CERRADO">Cerrado</option>
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="contrato">Contrato</Label>
              <Input id="contrato" value={formData.contrato} onChange={(e) => handleChange('contrato', e.target.value)} placeholder="N° de contrato" data-testid="input-contrato" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="fecha_inicio">Fecha Inicio</Label>
              <Input id="fecha_inicio" type="date" value={formData.fecha_inicio} onChange={(e) => handleChange('fecha_inicio', e.target.value)} data-testid="input-fecha-inicio" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="fecha_termino">Fecha Término</Label>
              <Input id="fecha_termino" type="date" value={formData.fecha_termino} onChange={(e) => handleChange('fecha_termino', e.target.value)} data-testid="input-fecha-termino" />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-4 mt-6">
          <Button type="button" variant="outline" onClick={() => navigate(-1)} data-testid="cancel-btn">Cancelar</Button>
          <Button type="submit" disabled={saving} data-testid="save-btn">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <Save className="w-4 h-4 mr-2" />
            {isEdit ? 'Actualizar' : 'Crear'} Proyecto
          </Button>
        </div>
      </form>
    </div>
  );
};
